import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { RouteComponentProps, useLocation } from "react-router-dom";
import ReactGA from "react-ga4";
import { HomeFilled, ArrowLeftOutlined } from "@ant-design/icons";
import { SyncOutlined } from "src/icons";
import styles from "src/scenes/India2MIS/Home/index.module.scss";
import PinnedContainer from "src/shared/components/India2MIS/PinnedContainer";
import { fetchHome, searchPin, searchReset, FetchHomePayload, SearchPinPayload } from "src/store/home";
import { fetchAllOrgMetrics, FetchAllOrgMetricsPayload } from "src/store/allOrg";
import { setCategoriesFilters, categoryReset } from "src/store/category";
import { setFilters, clearDetails } from "src/store/details";
import {
  changeTimeTabAppWide,
  UserRoles,
  setIsRoleCalloutShownAction,
  setComingBackFromDetailsPage,
} from "src/store/app";
import { logoutAction } from "src/store/login";
import RibbonSlider from "src/shared/components/RibbonSlider";
import { useRedux } from "src/shared/hooks";
import Search from "src/shared/components/Search";
import { IRootState, IconClass } from "src/shared/types";
import { IReduxOperations, IActionFactory } from "src/store";
import userService from "src/shared/services/user.service";
import { noop, safeGet } from "src/shared/utils";
import Permissions from "../../AllOrg/Permissions";
import { devLogging } from "src/utils/logger";
import { Button, Spin } from "antd";
import TopNavbar, { MenuItem } from "src/shared/components/Navbar/NewNavbar";
import { truncate, reduxOperationToStatus, mergeStatuses } from "src/shared/utils";
import { fetchRestaurantData } from "src/utils/restaurantUtils";
// import FilterByRestaurant from "src/scenes/India2MIS/Home/FilterbyRestaurant";

const CALL_DELAY = 1500;

interface IState {
  metrics: IReduxOperations;
  search: IReduxOperations;
  selectedTab: any;
  userRoles: IReduxOperations<UserRoles>;
  isRoleCalloutShown: boolean;
}

type MappedActionProps = {
  fetchHome(payload: FetchHomePayload);
  searchPin(payload: SearchPinPayload);
  categoryReset: IActionFactory<symbol, unknown>;
  searchReset: IActionFactory<symbol, unknown>;
  logoutAction();
  clearDetails: IActionFactory<symbol, any>;
  changeTimeTabAppWide: IActionFactory<symbol, string>;
  setIsRoleCalloutShown: IActionFactory<symbol, boolean>;
  fetchAllOrgMetrics(payload?: FetchAllOrgMetricsPayload);
  setComingBackFromDetailsPage: IActionFactory<symbol, boolean>;
};

const MainContainer: React.FC<RouteComponentProps> = ({ history }) => {
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: window.location.pathname, title: "India2MIS Third Screen" });

    // Ensure we have the correct default tab on mount
    if (!timeTabForIndia2MIS) {
      setTimeTabForIndia2MIS("Today_india_2");
    }

    // Ensure the app-wide tab is in sync with our component
    mappedProps.changeTimeTabAppWide(timeTabForIndia2MIS);

    return () => {
      document.body.style.overflowY = "auto";
      isInitialFetchDone.current = false;
    };
  }, []);
  const [page, setPage] = useState(1);
  const [timeTabForIndia2MIS, setTimeTabForIndia2MIS] = useState("Today_india_2");
  const containerRef = useRef<HTMLDivElement>(null as any);
  const isInitialFetchDone = useRef(false);

  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const restaurant_id_filter_value = queryParams.get("restaurant_id") || "";
  const restaurant_id_filter = restaurant_id_filter_value
    ? [
        {
          field: "restaurant_id",
          value: [restaurant_id_filter_value],
        },
      ]
    : [];

  const [selectedRestaurant, setSelectedRestaurant] = useState<string[] | null>([restaurant_id_filter_value]);
  const showDetails = useCallback(
    (id, name) => {
      mappedProps.setComingBackFromDetailsPage(true);

      history.push(`/india2-mis-app/details/fourth-screen/pin/${restaurant_id_filter_value}/${id}`);
      ReactGA.event({
        category: "Pin Clicked on L1a",
        label: `Pin: ${name} on L1a`,
        action: "Click from L1a",
      });
    },
    [history]
  );

  const [showRibbon, setShowRibbon] = useState(false);

  const { clearDetails: clearL3Data, ...mappedProps } = useRedux<IRootState, IState, MappedActionProps, {}>(
    (state) => ({
      metrics: state.home.pinnedMetrics.metrics,
      search: state.home.pinnedMetrics.search,
      loadMorePending: state.home.pinnedMetrics.loadMorePending,
      chartsDataPending: state.home.pinnedMetrics.chartsDataPending,
      selectedTab: state.app.selectedTab,
      comingBackFromDetailsPage: state.app.comingBackFromDetailsPage,
      userRoles: state.app.userRoles,
      isRoleCalloutShown: state.app.isRoleCalloutShown,
    }),
    {
      fetchHome,
      searchPin,
      searchReset,
      logoutAction,
      categoryReset,
      clearDetails,
      changeTimeTabAppWide,
      setIsRoleCalloutShown: setIsRoleCalloutShownAction,
      fetchAllOrgMetrics,
      setComingBackFromDetailsPage,
    }
  );

  const handleChangeTab = useCallback(
    ({ label }: { label: string }) => {
      if (!label) return;

      setIsInitialLoading(true);

      setPage(1);
      setTimeTabForIndia2MIS(label);

      mappedProps.changeTimeTabAppWide(label);

      setTimeout(() => {
        setIsInitialLoading(false);
      }, 500);
    },
    [mappedProps.changeTimeTabAppWide]
  );

  const onLogout = useCallback(() => {
    userService.logout();
    history.push("/");
    mappedProps.logoutAction();
  }, []);

  const safeFetchHome = useCallback(
    (params) => {
      if (
        timeTabForIndia2MIS &&
        params.filters &&
        params.filters.length > 0 &&
        params.filters[0].value &&
        params.filters[0].value.length > 0
      ) {
        mappedProps.fetchHome({
          ...params,
          tabName: timeTabForIndia2MIS,
        });
      }
    },
    [mappedProps.fetchHome, timeTabForIndia2MIS]
  );

  const handleRestaurantChange = useCallback(
    (value: string[]) => {
      if (!value || (value.length === 0 && !mappedProps.userRoles.payload?.restaurantIds?.length)) {
        return;
      }

      setIsInitialLoading(true);
      setSelectedRestaurant(value);

      const filters =
        value.length > 0
          ? [
              {
                field: "restaurant_id",
                value: value,
              },
            ]
          : [
              {
                field: "restaurant_id",
                value: mappedProps.userRoles.payload?.restaurantIds || [],
              },
            ];

      setPage(1);
      safeFetchHome({
        pageNo: 1,
        fresh: true,
        filters,
        tabName: timeTabForIndia2MIS,
      });

      setTimeout(() => {
        setIsInitialLoading(false);
      }, 500);
    },
    [safeFetchHome, mappedProps.userRoles.payload?.restaurantIds, timeTabForIndia2MIS]
  );

  const loadMore = useCallback(() => {
    if (
      !(safeGet(mappedProps, "metrics.pending", false) || safeGet(mappedProps, "loadMorePending", false)) &&
      safeGet(mappedProps, "metrics.payload.hasMore", false)
    ) {
      const restaurantValue =
        selectedRestaurant && selectedRestaurant.length > 0
          ? selectedRestaurant
          : mappedProps.userRoles.payload?.restaurantIds || [];

      if (restaurantValue && restaurantValue.length > 0) {
        const filters = [
          {
            field: "restaurant_id",
            value: restaurantValue,
          },
        ];
        safeFetchHome({ pageNo: page + 1, filters });
        setPage(page + 1);
      }
    }
  }, [
    mappedProps.metrics,
    safeGet(mappedProps, "loadMorePending"),
    page,
    selectedRestaurant,
    mappedProps.userRoles.payload?.restaurantIds,
    safeFetchHome,
  ]);

  const [isRefreshClicked, setIsRefreshClicked] = useState(0);
  const [shouldShowPermissions, setShouldShowPermissions] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [visibleMetrics, setVisibleMetrics] = useState(mappedProps.metrics);
  const [restaurantName, setRestaurantName] = useState("");
  const [restaurantMap, setRestaurantMap] = useState<Record<string, string>>({});

  useEffect(() => {
    setVisibleMetrics(mappedProps.metrics);
  }, [mappedProps.metrics]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (restaurant_id_filter.length > 0 && !isInitialFetchDone.current) {
      if (restaurant_id_filter[0].value && restaurant_id_filter[0].value.length > 0) {
        handleRestaurantChange(restaurant_id_filter[0].value);
        isInitialFetchDone.current = true;
      }
    }
  }, []);

  useEffect(() => {
    if (!timeTabForIndia2MIS) {
      setTimeTabForIndia2MIS("Today_india_2");
    }
  }, []);

  useEffect(() => {
    if (isInitialFetchDone.current && timeTabForIndia2MIS) {
      const filters =
        selectedRestaurant && selectedRestaurant.length > 0
          ? [
              {
                field: "restaurant_id",
                value: selectedRestaurant,
              },
            ]
          : restaurant_id_filter.length > 0
          ? restaurant_id_filter
          : [
              {
                field: "restaurant_id",
                value: mappedProps.userRoles.payload?.restaurantIds || [],
              },
            ];

      setPage(1);
      safeFetchHome({ pageNo: 1, fresh: true, filters });
    }
  }, [timeTabForIndia2MIS, safeFetchHome]);

  useEffect(() => {
    // Fetch restaurant data once on component mount
    const loadRestaurantData = async () => {
      try {
        const restaurants = await fetchRestaurantData();
        // Create a mapping of restaurant_id to restaurant_name
        const mapping: Record<string, string> = {};
        restaurants.forEach((restaurant) => {
          mapping[restaurant.restaurant_id] = restaurant.restaurant_name;
        });
        setRestaurantMap(mapping);

        // Set restaurant name if ID is available
        if (restaurant_id_filter_value && mapping[restaurant_id_filter_value]) {
          setRestaurantName(mapping[restaurant_id_filter_value]);
        }
      } catch (error) {
        console.error("Failed to load restaurant data:", error);
      }
    };

    loadRestaurantData();
  }, []);

  // Update restaurant name when ID changes or mapping is updated
  useEffect(() => {
    if (restaurant_id_filter_value && restaurantMap[restaurant_id_filter_value]) {
      setRestaurantName(restaurantMap[restaurant_id_filter_value]);
    } else if (restaurant_id_filter_value) {
      setRestaurantName("Unknown Restaurant");
    }
  }, [restaurant_id_filter_value, restaurantMap]);

  type OptionsType = {
    className: string;
    iconClass: string;
    handleClick(): void;
  };
  type OptionGroupType = Array<OptionsType>;
  const options: OptionGroupType = [
    {
      className: "option1",
      iconClass: "home",
      handleClick: () => {
        history.push("/india-2-app");
        window.location.reload();
      },
    },
    {
      className: "option3",
      iconClass: "sync",
      handleClick: () => {
        if (
          restaurant_id_filter &&
          restaurant_id_filter.length > 0 &&
          restaurant_id_filter[0].value &&
          restaurant_id_filter[0].value.length > 0
        ) {
          setIsRefreshClicked(Date.now());
          setPage(1);
          safeFetchHome({ pageNo: 1, fresh: true, filters: restaurant_id_filter });
        }
      },
    },
  ];
  const onBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const meticOptionsV1 = useMemo(() => {
    return mappedProps.metrics.payload?.data.map((metric) => ({
      value: metric.label,
      label: metric.label,
    }));
  }, [mappedProps.metrics]);

  return (
    <>
      <div className={styles["container"]}>
        <TopNavbar
          isHome={false}
          handleClick={onBack}
          headerText={restaurant_id_filter_value + ""}
          rootStyle="padding: 10px; color: black; "
        >
          <p style={{ marginLeft: -1 }}>
            {restaurant_id_filter_value} - {restaurantName ? truncate(restaurantName, 20) : "Loading..."}
          </p>
          {options.map((option) => (
            <MenuItem key={option.iconClass} {...option} showMarker={option.iconClass === "sliders"} />
          ))}
          {/* {meticOptionsV1 && (
            <FilterByRestaurant
              selectedRestaurant={selectedRestaurant || undefined}
              handleRestaurantChange={handleRestaurantChange}
              restaurantOptions={[]} // Empty array to disable restaurant filter
              cityOptions={[]} // Empty array to disable city filter
              selectedCity={undefined}
              handleSelectedCity={() => {}}
              filterType=""
              setFilterType={() => {}}
              disableRestaurantFilter={true}
              disableCityFilter={true}
              disableClearFilterButton={true}
            />
          )} */}
        </TopNavbar>

        {isInitialLoading ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
            <Spin size="large" tip="Loading metrics..." />
          </div>
        ) : (
          <div className={styles["pinned-items"]} style={{ opacity: showRibbon ? 0 : 1 }}>
            <PinnedContainer
              isPulledUp={showRibbon}
              pins={visibleMetrics}
              showDetails={showDetails}
              isPending={safeGet(mappedProps, "metrics.pending", false)}
              onChangeTab={handleChangeTab}
              switchToManageMetrics={() => {}}
              selectedTab={timeTabForIndia2MIS}
              brokenPodsVisibleTabs={[]}
            />
            {safeGet(mappedProps, "metrics.payload.hasMore", false) && (
              <Button type="primary" shape="round" className={styles["load-more-button"]} onClick={loadMore}>
                Load more
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default MainContainer;
